import React, { useState, useEffect } from 'react';
import { X, Filter, CheckCircle, AlertCircle } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import Button from '@shared/ui/Button';
import { api } from '../App';
import { formatPartyDisplay, getPartyBadgeColor, matchesPartyFilter } from '../utils/partyUtils';
import DistrictMapLink from './DistrictMapLink';
import { prefetchHostedDistrictMapUrls } from '../utils/districtMapUrls';
import { PARTY_REGISTRY } from '../constants/parties';
import { formatDistrict } from '../utils/districtDisplay';

interface BulkVotePredictionModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  billId: string;
  onSuccess: () => void;
}

interface FilterCriteria {
  parties?: string[];
  chambers?: string[];
  committees?: string[];
}


const BulkVotePredictionModal: React.FC<BulkVotePredictionModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  billId,
  onSuccess
}) => {
  const [step, setStep] = useState<'filters' | 'preview' | 'assign'>('filters');
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({});
  const [vote, setVote] = useState<'yes' | 'no' | 'abstain' | 'absent'>('yes');
  const [confidence, setConfidence] = useState<'low' | 'medium' | 'high'>('medium');
  const [notes, setNotes] = useState('');
  const [matchingLegislators, setMatchingLegislators] = useState<any[]>([]);
  // One batched district-map lookup for the preview list instead of one per row (#1179).
  useEffect(() => {
    if (matchingLegislators.length > 0) void prefetchHostedDistrictMapUrls(matchingLegislators.slice(0, 10));
  }, [matchingLegislators]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Available filter options — canonical labels from the party registry
  const parties = ['republican', 'democratic', 'independent', 'libertarian']
    .map((id) => PARTY_REGISTRY.find((e) => e.id === id)?.displayLabel)
    .filter((label): label is string => Boolean(label));

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      setStep('filters');
      setFilterCriteria({});
      setVote('yes');
      setConfidence('medium');
      setNotes('');
      setMatchingLegislators([]);
      fetchCommittees();
    }
  }, [isOpen]);

  // Fetch committees for filter options
  const fetchCommittees = async () => {
    try {
      // Note: Committee filtering is not yet available for bulk vote predictions.
      // Implementation requires: GET /api/committees endpoint with chamber filtering support.
    } catch (error) {
      console.error('Error fetching committees:', error);
    }
  };

  // Preview matching legislators
  const previewLegislators = async () => {
    if (Object.keys(filterCriteria).length === 0) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/workspace-vote-predictions/${workspaceId}/legislators-for-bill/${billId}`);
      const allLegislators = response.data.data;

      // Apply filters
      let filtered = allLegislators;

      if (filterCriteria.parties?.length) {
        filtered = filtered.filter((leg: any) =>
          filterCriteria.parties!.some(party =>
            matchesPartyFilter(leg.party_affiliation, party)
          )
        );
      }

      if (filterCriteria.chambers?.length) {
        filtered = filtered.filter((leg: any) =>
          filterCriteria.chambers!.includes(leg.chamber)
        );
      }

      if (filterCriteria.committees?.length) {
        filtered = filtered.filter((leg: any) =>
          filterCriteria.committees!.some(committeeId =>
            leg.committees.includes(committeeId) // This would need to be improved for actual committee matching
          )
        );
      }


      setMatchingLegislators(filtered);
      setStep('preview');
    } catch (error) {
      console.error('Error previewing legislators:', error);
    } finally {
      setLoading(false);
    }
  };

  // Submit bulk assignment
  const submitBulkAssignment = async () => {
    setSubmitting(true);
    try {
      await api.post('/workspace-vote-predictions/bulk', {
        workspaceId,
        billId: parseInt(billId),
        predictedVote: vote,
        confidenceLevel: confidence,
        predictionNotes: notes,
        filterCriteria
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting bulk assignment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle filter option
  const toggleFilterOption = (category: keyof FilterCriteria, value: any) => {
    setFilterCriteria(prev => {
      const current = prev[category] as any[] || [];
      const newArray = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      
      return {
        ...prev,
        [category]: newArray.length > 0 ? newArray : undefined
      };
    });
  };



  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      title="Bulk vote assignment"
      onClose={onClose}
      size="xl"
      /* The step indicator has to stay visible — it is the only thing telling
         you which of the two screens you are on. */
      subheader={
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${step === 'filters' ? 'text-primary-600' : 'text-neutral-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'filters' ? 'bg-primary-600 text-white' : 'bg-background-tertiary text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary'
              }`}>
                1
              </div>
              <span className="ml-2 font-medium">Select Filters</span>
            </div>
            <div className={`flex items-center ${step === 'preview' ? 'text-primary-600' : 'text-neutral-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'preview' ? 'bg-primary-600 text-white' : 'bg-background-tertiary text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary'
              }`}>
                2
              </div>
              <span className="ml-2 font-medium">Preview & Assign</span>
            </div>
          </div>
      }
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => (step === 'preview' ? setStep('filters') : onClose())}
          >
            {step === 'preview' ? 'Back' : 'Cancel'}
          </Button>
          {step === 'filters' ? (
            <Button
              variant="primary"
              onClick={previewLegislators}
              loading={loading}
              disabled={Object.keys(filterCriteria).length === 0 || loading}
            >
              Preview matches
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={CheckCircle}
              onClick={submitBulkAssignment}
              loading={submitting}
              disabled={matchingLegislators.length === 0 || submitting}
            >
              Apply to {matchingLegislators.length} legislators
            </Button>
          )}
        </>
      }
    >
          {step === 'filters' && (
            <div className="space-y-6">
              <div className="text-sm text-neutral-600 dark:text-text-dark-tertiary mb-4">
                Select criteria to filter legislators for bulk vote assignment:
              </div>

              {/* Party Filter */}
              <div>
                <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-3">
                  <Filter className="w-4 h-4 inline mr-2" />
                  Party Affiliation
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {parties.map(party => (
                    <label key={party} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterCriteria.parties?.includes(party) || false}
                        onChange={() => toggleFilterOption('parties', party)}
                        className="rounded border-border-light-secondary text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-neutral-700 dark:text-text-dark-secondary">{party}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Members Filter */}
              <div>
                <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-3">
                  <Filter className="w-4 h-4 inline mr-2" />
                  Members
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 p-4 border border-border-light-secondary dark:border-border-dark-secondary rounded-lg cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors">
                    <input
                      type="checkbox"
                      checked={filterCriteria.chambers?.includes('House') || false}
                      onChange={() => toggleFilterOption('chambers', 'House')}
                      className="rounded border-border-light-secondary text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">
                        House Members
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                        All House of Representatives members
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center space-x-3 p-4 border border-border-light-secondary dark:border-border-dark-secondary rounded-lg cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors">
                    <input
                      type="checkbox"
                      checked={filterCriteria.chambers?.includes('Senate') || false}
                      onChange={() => toggleFilterOption('chambers', 'Senate')}
                      className="rounded border-border-light-secondary text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">
                        Senate Members
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                        All Senate members
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Active Filters Summary */}
              {Object.keys(filterCriteria).length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                    Active Filters:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {filterCriteria.parties?.map(party => (
                      <button
                        key={party}
                        onClick={() => toggleFilterOption('parties', party)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800 rounded text-xs transition-colors"
                      >
                        {party}
                        <X className="w-3 h-3" />
                      </button>
                    ))}
                    {filterCriteria.chambers?.map(chamber => (
                      <button
                        key={chamber}
                        onClick={() => toggleFilterOption('chambers', chamber)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800 rounded text-xs transition-colors"
                      >
                        {chamber} Members
                        <X className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              {/* Matching Legislators */}
              <div>
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
                  Matching Legislators ({matchingLegislators.length})
                </h3>
                
                {matchingLegislators.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-neutral-500 mx-auto mb-3" />
                    <p className="text-neutral-600 dark:text-text-dark-tertiary">
                      No legislators match the selected criteria.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {matchingLegislators.slice(0, 10).map(legislator => (
                      <div key={legislator.id} className="flex items-center justify-between p-3 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {legislator.full_name}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPartyBadgeColor(legislator.party_affiliation)}`}>
                            {formatPartyDisplay(legislator.party_affiliation)}
                          </span>
                          <span className="text-sm text-neutral-600 dark:text-text-dark-tertiary">
                            {legislator.chamber}{' '}
                            <DistrictMapLink
                              chamber={legislator.chamber}
                              district={legislator.district}
                              className="text-sm text-neutral-600 dark:text-text-dark-tertiary"
                            >
                              {formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)}
                            </DistrictMapLink>
                          </span>
                        </div>
                      </div>
                    ))}
                    {matchingLegislators.length > 10 && (
                      <p className="text-sm text-neutral-600 dark:text-text-dark-tertiary text-center pt-2">
                        And {matchingLegislators.length - 10} more legislators...
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Vote Assignment */}
              {matchingLegislators.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                    Assign Vote
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                        Predicted Vote
                      </label>
                      <select
                        value={vote}
                        onChange={(e) => setVote(e.target.value as any)}
                        className="input"
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="abstain">Abstain</option>
                        <option value="absent">Absent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                        Confidence Level
                      </label>
                      <select
                        value={confidence}
                        onChange={(e) => setConfidence(e.target.value as any)}
                        className="input"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Bulk Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="input"
                      placeholder="Add notes that will apply to all selected legislators..."
                    />
                  </div>
                </div>
              )}
            </div>
          )}

    </Modal>
  );
};

export default BulkVotePredictionModal;
